import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import size from 'lodash/size'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { schemaNumber } from 'schemas/baseSchema'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { CryptoRewardsPaneSumPicker } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPaneSumPicker/CryptoRewardsPaneSumPicker'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CRYPTO_REWARDS_BUY_MODE_STEP_CURRENCY_LIST, CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'
import { CryptoRewardsBuyModeFields } from 'components/features/rewards/rewardsPane/types/paneStateBuyMode'
import { CRYPTO_CURRENCY_INVESTMENT_MODE_AUTO, CRYPTO_CURRENCY_INVESTMENT_MODE_MANUAL } from 'components/features/rewards/rewardsPane/constants/investmentMode'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { getAutoinvestOrDefaultCurrencyIdOrNullSelector, getUserRewardsSumUSDSelector } from 'components/features/rewards/rewardsPane/store/selectors/cryptoRewards'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import {
  getCryptoRewardsPaneFieldsStateBuyModeOrNullSelector,
  getCryptoRewardsPaneProgressPercentsStateSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { BuyCryptoAction } from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { isCryptocurrenciesStaticMetaLoadingSelector } from 'components/features/rewards/rewardsPane/store/selectors/currencies'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import { FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { getBuyOrderFailedResultOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { useServerErrorsApplyToForm } from 'hooks/useServerErrorsApplyToForm'
import { OrderResultsReducerResetLastOrderErrorsAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { GeneralErrors } from 'interfaces/errors'
import { OrderFailedResult } from 'components/features/rewards/rewardsPane/types/orderResult'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { REQUIRED_TEXT } from 'constants/errors'
import { CryptoRewardsPaneLinkButton } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPaneLinkButton/CryptoRewardsPaneLinkButton'
import { useMinBuyCryptoAmount } from 'components/features/rewards/rewardPage/useBalance'
import { cryptoMarketGuard } from 'components/features/rewards/rewardsPane/guards/cryptoCurrenciesMarketDataLoadedGuard'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { CurrenciesMetaGetMarketDataRequestAction } from 'components/features/rewards/rewardsPane/store/actions/currenciesStatistics'
import Button from 'components/features/pfm/Button/Button'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'


export type CryptoAutoInvestFields = {
  enable_autoinvest: boolean
  amount_usd: number
  ticker: string
}

type CryptoRewardsBuyAmountStepProps = {
  onClose: () => void
}

const STEP_TITLE = 'Invest Your Rewards Into Crypto'

const STEP_SUBTITLE = 'How much would you like to invest?'

const FIELD_INPUT_SUM_NAME = 'amount_usd'

const FIELD_CHECKBOX_AUTO_INVEST_NAME = 'enable_autoinvest'

const CryptoRewardsBuyAmountStepInner: FC<CryptoRewardsBuyAmountStepProps> = ({ onClose }) => {
  const dispatch = useStoreDispatch()
  const [formGeneralErrorsOrNull, setFormGeneralErrorsOrNull] = useState<GeneralErrors | null>(null)
  const formGeneralErrorToRender: [string] | null = useMemo(() => formGeneralErrorsOrNull ? [formGeneralErrorsOrNull[0]] : null, [formGeneralErrorsOrNull])
  const buyOrderErrorsOrNull: OrderFailedResult<typeof FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE> | null = useStoreSelector(getBuyOrderFailedResultOrNullSelector)
  const isCurrenciesMetaLoading: boolean = useStoreSelector(isCryptocurrenciesStaticMetaLoadingSelector)
  const paneProgressPercent: number = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const paneFieldsStateOrNull: Partial<CryptoRewardsBuyModeFields> | null = useStoreSelector(getCryptoRewardsPaneFieldsStateBuyModeOrNullSelector)
  const wholeRewardsSumUSD: number = useStoreSelector(getUserRewardsSumUSDSelector)
  const cryptoCurrencyIdForAutoinvestmentsOrNull: CryptoCurrencyId | null = useStoreSelector(getAutoinvestOrDefaultCurrencyIdOrNullSelector)
  const cryptoCurrencyIdForBuying: CryptoCurrencyId | null = paneFieldsStateOrNull?.cryptoCurrencyIDChosenForInvestment || cryptoCurrencyIdForAutoinvestmentsOrNull
  const cryptoCurrencyName: string = useGetCryptoCurrencyNameById(cryptoCurrencyIdForBuying)

  const minSellAmount = useMinBuyCryptoAmount(cryptoCurrencyIdForBuying) || '0'

  const changeCryptoRewardsState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE)

  const defaultInvestmentsAmountUSD = Number(paneFieldsStateOrNull?.investmentsAmountUSD ?? wholeRewardsSumUSD)
  const isAutoInvestmentsFeatureEnabledByDefault: boolean = (
    paneFieldsStateOrNull?.cryptoCurrencyInvestmentsModeChosen == null
      ? true // TODO - enabled by default
      : paneFieldsStateOrNull.cryptoCurrencyInvestmentsModeChosen === 'auto'
  )
  const formValidationSchemaYup = useMemo(() => (
    yup.object({
      [FIELD_INPUT_SUM_NAME]: schemaNumber
        .required(REQUIRED_TEXT)
        .min(+minSellAmount, `Minimum investment limit is ${ formatUSCurrency(minSellAmount, 'penny') }`)
        .max(wholeRewardsSumUSD, `Only ${ formatUSCurrency(wholeRewardsSumUSD, 'penny') } is available`),
    })
  ), [minSellAmount, wholeRewardsSumUSD])

  const formContext = useForm<CryptoAutoInvestFields>({
    resolver: yupResolver(formValidationSchemaYup),
    defaultValues: {
      [FIELD_INPUT_SUM_NAME]: defaultInvestmentsAmountUSD,
      [FIELD_CHECKBOX_AUTO_INVEST_NAME]: isAutoInvestmentsFeatureEnabledByDefault,
    },
  })

  const formFieldsErrorsOrNull = buyOrderErrorsOrNull?.fieldsErrors ?? null
  useServerErrorsApplyToForm(formContext, formFieldsErrorsOrNull ?? {})

  const {
    formState,
    watch,
    clearErrors,
    handleSubmit,
    setValue,
    trigger,
  } = formContext
  const { dirtyFields, errors: formFieldsContextErrors } = formState
  const investmentsSum: number = watch(FIELD_INPUT_SUM_NAME)
  const isAutoInvestmentFeatureEnabled = watch(FIELD_CHECKBOX_AUTO_INVEST_NAME)
  const isFieldInputSumDirty = Boolean(dirtyFields[FIELD_INPUT_SUM_NAME])
  const mainText = useMemo<ReactElement>(() => (
    <small>
      When you submit this request, we’ll invest your rewards into { cryptoCurrencyName } at the current market rate. There
      are no fees to purchase crypto and you can sell to convert your rewards back to cash at any time. If you turn on
      auto-investing, we’ll automatically invest your future rewards in { cryptoCurrencyName } at the then current market rate. You
      can turn off auto-investing at any time. All investments involve risk, cryptocurrency investments are not FDIC or SIPC insured and may lose value.
    </small>
  ), [cryptoCurrencyName])

  const sumPickerLabel = useMemo<string>(() => `Available rewards: ${ formatUSCurrency(wholeRewardsSumUSD, 'penny') }`, [wholeRewardsSumUSD])
  const sumPicker = useMemo<ReactElement>(() => (
    <CryptoRewardsPaneSumPicker name={ FIELD_INPUT_SUM_NAME } note={ sumPickerLabel } />
  ), [sumPickerLabel])

  useEffect(() => {
    trigger()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoCurrencyIdForBuying])

  const handleAutoInvestChanged = useCallback(() => {
    setValue(FIELD_INPUT_SUM_NAME, wholeRewardsSumUSD)
    trigger()
  }, [wholeRewardsSumUSD, setValue, trigger])
  const autoInvestFeatureSwitcher = useMemo<ReactElement>(() => (
    <LabeledInput
      name={ FIELD_CHECKBOX_AUTO_INVEST_NAME }
      label='Auto-invest all rewards into crypto'
      type='checkbox'
    />
  ), [])

  useEffect(() => {
    isAutoInvestmentFeatureEnabled && handleAutoInvestChanged()
  }, [handleAutoInvestChanged, isAutoInvestmentFeatureEnabled])

  const updatedPaneFieldsState = useMemo<
    Omit<Required<CryptoRewardsBuyModeFields>, 'cryptoCurrencyIDChosenForInvestment'>
    & Omit<Partial<CryptoRewardsBuyModeFields>, 'cryptoCurrencyIDChosenForInvestment'>
  >(() => ({
    investmentsAmountUSD: `${ (+investmentsSum || 0).toFixed(2) }`,
    cryptoCurrencyIDChosenForInvestment: cryptoCurrencyIdForBuying,
    cryptoCurrencyInvestmentsModeChosen: isAutoInvestmentFeatureEnabled
      ? CRYPTO_CURRENCY_INVESTMENT_MODE_AUTO
      : CRYPTO_CURRENCY_INVESTMENT_MODE_MANUAL,
  }), [investmentsSum, cryptoCurrencyIdForBuying, isAutoInvestmentFeatureEnabled])

  const handleOpenCurrencyPickerStep = useCallback((): void => {
    const paneUpdatedState = {
      step: CRYPTO_REWARDS_BUY_MODE_STEP_CURRENCY_LIST,
      fields: updatedPaneFieldsState,
    }
    changeCryptoRewardsState(paneUpdatedState)
  }, [updatedPaneFieldsState, changeCryptoRewardsState])
  const openCryptoCurrencyPickerBtn = useMemo<ReactElement>(() => (
    <CryptoRewardsPaneLinkButton onClick={ handleOpenCurrencyPickerStep }>
      Pick another crypto
    </CryptoRewardsPaneLinkButton>
  ), [handleOpenCurrencyPickerStep])

  const handleCreateOrder = useMemo(() => handleSubmit(() => {
    if (!cryptoCurrencyIdForBuying) {
      return
    }
    const paneUpdatedState = {
      step: CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT,
      fields: {
        ...updatedPaneFieldsState,
        cryptoCurrencyIDChosenForInvestment: cryptoCurrencyIdForBuying,
      },
    }
    changeCryptoRewardsState(paneUpdatedState)
    dispatch(new BuyCryptoAction({
      ...paneUpdatedState,
      mode: 'cryptoRewardsBuyMode',
    }))
  }), [handleSubmit, cryptoCurrencyIdForBuying, updatedPaneFieldsState, changeCryptoRewardsState, dispatch])

  const handleErrorsLayoutClosed = useCallback(() => {
    setFormGeneralErrorsOrNull(null)
  }, [setFormGeneralErrorsOrNull])

  useEffect(() => {
    if (wholeRewardsSumUSD && isFieldInputSumDirty && investmentsSum < wholeRewardsSumUSD) {
      setValue(FIELD_CHECKBOX_AUTO_INVEST_NAME, false)
    }
  }, [wholeRewardsSumUSD, investmentsSum, isFieldInputSumDirty, setValue])

  useEffect(() => {
    if (buyOrderErrorsOrNull) {
      const { generalErrors, fieldsErrors } = buyOrderErrorsOrNull
      if (generalErrors?.length) {
        setFormGeneralErrorsOrNull(generalErrors)
      } else if (fieldsErrors?.ticker?.length) {
        setFormGeneralErrorsOrNull(fieldsErrors.ticker)
      }
      clearErrors('ticker')
      dispatch(new OrderResultsReducerResetLastOrderErrorsAction())
    }
  }, [buyOrderErrorsOrNull, clearErrors, dispatch])

  const isNextStepButtonDisabled = !cryptoCurrencyIdForBuying || Boolean(size(formFieldsContextErrors))

  return (
    <CryptoRewardsPanePreset
      isLoading={ isCurrenciesMetaLoading }
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      actionDisabled={ isNextStepButtonDisabled }
      progressBarPercent={ paneProgressPercent }
      errors={ formGeneralErrorToRender }
      onCancel={ onClose }
      onErrorsOverlayClosed={ handleErrorsLayoutClosed }
    >
      <FormPreset formContext={ formContext } onSubmit={ handleCreateOrder } >
        { sumPicker }
        { autoInvestFeatureSwitcher }
        { mainText }
        { openCryptoCurrencyPickerBtn }
        <Button text='Submit Investment' submit />
      </FormPreset>
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsBuyAmountStep: FC<CryptoRewardsBuyAmountStepProps> = ({ onClose }) => {
  const isMetaLoaded = useSimpleGuard(cryptoMetaGuard, {}, 'CryptoRewardsBuyAmountStep')
  const isMarketLoaded = useSimpleGuard(cryptoMarketGuard, {}, 'CryptoRewardsBuyAmountStep')

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(new CurrenciesMetaGetMarketDataRequestAction())
  }, [dispatch])

  if (!isMetaLoaded || !isMarketLoaded) {
    return (
      <CryptoRewardsPanePreset
        actionName='SUBMIT INVESTMENT'
        title={ STEP_TITLE }
        subTitle={ STEP_SUBTITLE }
      >
        <LoadingOverlay />
      </CryptoRewardsPanePreset>
    )
  }

  return (
    <CryptoRewardsBuyAmountStepInner onClose={ onClose } />
  )
}
